// src/services/data/store.ts
import { Gathering, GatheringStatus, Space, FabrikUser } from '../../types';

// Define the store structure
export interface NormalizedStore {
  entities: {
    gatherings: Record<string, Gathering>;
    spaces: Record<string, Space>;
    users: Record<string, FabrikUser>;
  };
  views: {
    byStatus: Record<GatheringStatus, string[]>;
    byDate: Record<string, string[]>; // Format: YYYY-MM-DD
  };
  metadata: {
    isLoading: {
      byStatus: Record<GatheringStatus, boolean>;
      byDate: Record<string, boolean>;
      spaces: boolean;
      users: boolean;
    };
    isLoadingAllUsers: boolean;
    lastFetched: {
      byStatus: Record<GatheringStatus, number>;
      byDate: Record<string, number>;
      spaces: number;
      users: number;
    };
    pagination: {
      byStatus: Record<GatheringStatus, {
        currentPage: number;
        totalPages: number;
        hasMore: boolean;
        isLoadingAllPages?: boolean; // Optional flag to track multi-page loads
      }>;
      users: {
        currentPage: number;
        totalPages: number;
        hasMore: boolean;
      };
    };
  };
}

// Define types for the subscription mechanism
type StoreListener = () => void;

// Array to store listener functions
let listeners: StoreListener[] = [];

/**
 * Subscribe to store changes
 * @param listener Function to call when store changes
 * @returns Unsubscribe function
 */
export const subscribe = (listener: StoreListener): (() => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

/**
 * Notify all listeners of store changes
 */
export const notifyListeners = (): void => {
  listeners.forEach(listener => listener());
};

// Create initial store state
export const createInitialStore = (): NormalizedStore => {
  // Create empty status arrays for all gathering statuses
  const emptyStatusViews: Record<GatheringStatus, string[]> = Object.values(GatheringStatus).reduce(
    (acc, status) => {
      acc[status] = [];
      return acc;
    },
    {} as Record<GatheringStatus, string[]>
  );

  // Create empty loading state for all gathering statuses
  const emptyLoadingState: Record<GatheringStatus, boolean> = Object.values(GatheringStatus).reduce(
    (acc, status) => {
      acc[status] = false;
      return acc;
    },
    {} as Record<GatheringStatus, boolean>
  );

  // Create empty last fetched timestamps for all gathering statuses
  const emptyLastFetched: Record<GatheringStatus, number> = Object.values(GatheringStatus).reduce(
    (acc, status) => {
      acc[status] = 0;
      return acc;
    },
    {} as Record<GatheringStatus, number>
  );

  // Create empty pagination state for all gathering statuses
  const emptyPagination: Record<GatheringStatus, { currentPage: number; totalPages: number; hasMore: boolean; isLoadingAllPages?: boolean }> = 
    Object.values(GatheringStatus).reduce(
      (acc, status) => {
        acc[status] = { currentPage: 0, totalPages: 0, hasMore: false };
        return acc;
      },
      {} as Record<GatheringStatus, { currentPage: number; totalPages: number; hasMore: boolean; isLoadingAllPages?: boolean }>
    );

  return {
    entities: {
      gatherings: {},
      spaces: {},
      users: {}
    },
    views: {
      byStatus: emptyStatusViews,
      byDate: {}
    },
    metadata: {
      isLoading: {
        byStatus: emptyLoadingState,
        byDate: {},
        spaces: false,
        users: false
      },
      isLoadingAllUsers: false,
      lastFetched: {
        byStatus: emptyLastFetched,
        byDate: {},
        spaces: 0,
        users: 0
      },
      pagination: {
        byStatus: emptyPagination,
        users: { currentPage: 0, totalPages: 0, hasMore: false }
      }
    }
  };
};

// Create the singleton store instance
export const store: NormalizedStore = createInitialStore();

// Basic store operations
export const addGathering = (gathering: Gathering): void => {
  // Add to entities
  store.entities.gatherings[gathering.id] = gathering;
  
  // Add to status view if not already there
  if (!store.views.byStatus[gathering.status].includes(gathering.id)) {
    store.views.byStatus[gathering.status].push(gathering.id);
  }
  
  // Add to date view if not already there
  const dateKey = gathering.date.split('T')[0];
  if (!store.views.byDate[dateKey]) {
    store.views.byDate[dateKey] = [];
  }
  if (!store.views.byDate[dateKey].includes(gathering.id)) {
    store.views.byDate[dateKey].push(gathering.id);
  }
  
  // Notify listeners of the change
  notifyListeners();
};

export const updateGathering = (gathering: Gathering): void => {
  const existingGathering = store.entities.gatherings[gathering.id];
  
  // If status changed, update the status views
  if (existingGathering && existingGathering.status !== gathering.status) {
    // Remove from old status
    store.views.byStatus[existingGathering.status] = 
      store.views.byStatus[existingGathering.status].filter(id => id !== gathering.id);
    
    // Add to new status
    if (!store.views.byStatus[gathering.status].includes(gathering.id)) {
      store.views.byStatus[gathering.status].push(gathering.id);
    }
  }
  
  // If date changed, update the date views
  if (existingGathering && existingGathering.date !== gathering.date) {
    const oldDateKey = existingGathering.date.split('T')[0];
    const newDateKey = gathering.date.split('T')[0];
    
    // Remove from old date
    if (store.views.byDate[oldDateKey]) {
      store.views.byDate[oldDateKey] = 
        store.views.byDate[oldDateKey].filter(id => id !== gathering.id);
    }
    
    // Add to new date
    if (!store.views.byDate[newDateKey]) {
      store.views.byDate[newDateKey] = [];
    }
    if (!store.views.byDate[newDateKey].includes(gathering.id)) {
      store.views.byDate[newDateKey].push(gathering.id);
    }
  }
  
  // Update the entity
  store.entities.gatherings[gathering.id] = gathering;
  
  // Notify listeners of the change
  notifyListeners();
};

export const removeGathering = (gatheringId: string): void => {
  const gathering = store.entities.gatherings[gatheringId];
  if (!gathering) return;
  
  // Remove from status view
  store.views.byStatus[gathering.status] = 
    store.views.byStatus[gathering.status].filter(id => id !== gatheringId);
  
  // Remove from date view
  const dateKey = gathering.date.split('T')[0];
  if (store.views.byDate[dateKey]) {
    store.views.byDate[dateKey] = 
      store.views.byDate[dateKey].filter(id => id !== gatheringId);
  }
  
  // Remove from entities
  delete store.entities.gatherings[gatheringId];
  
  // Notify listeners of the change
  notifyListeners();
};

// User store operations
export const addUser = (user: FabrikUser): void => {
  // Add to entities
  store.entities.users[user.id] = user;
  
  // Notify listeners of the change
  notifyListeners();
};

export const updateUser = (user: FabrikUser): void => {
  // Update the entity
  store.entities.users[user.id] = user;
  
  // Notify listeners of the change
  notifyListeners();
};

export const removeUser = (userId: string): void => {
  // Remove from entities
  delete store.entities.users[userId];
  
  // Notify listeners of the change
  notifyListeners();
};

export const clearUsers = (): void => {
  // Clear all users from entities
  store.entities.users = {};
  
  // Notify listeners of the change
  notifyListeners();
};

export const clearStore = (): void => {
  // Reset the store to its initial state
  Object.assign(store, createInitialStore());
  
  // Notify listeners of the change
  notifyListeners();
};